import React, { useRef, useState } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { createResultPageSortResultPayload } from '@/app/common/starTrack/events/resultPageSortResult.ts';
import { useTranslation } from '@/app/components/translations/Translation';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';

import { useSearchCommonPageData } from '@/app/components/search/searchCommonPageProvider.tsx';
import { URLParameters } from '@/app/common/urlParameters.ts';
import ErrorBoundary from '@/app/common/components/ErrorBoundary';
import { SortValues } from '@/app/common/memsearch/types.ts';
import { Icon } from '@/app/common/icons/Icon';
import { getSearchParamsClient } from '../../../../../common/getSearchParams';
import getSortControl from './index';
import { Expand, ListViewSortContainer, Overlay, OverlayItem } from './styled';
import ListViewSortItem from './ListViewSortItem';

const ListViewSort = ({ isLoading }: { isLoading: boolean }) => {
  const { searchParams: searchParamsServer, searchInfoData, searchInfoDetailData, localization } = usePageWrapperData();
  const { isMobile, starTrack } = useCommonClient();
  const { handleSortChanged } = useSearchCommonPageData();
  const { t } = useTranslation();
  const { searchMode } = searchInfoData;
  const containerRef = useRef<HTMLButtonElement>(null) as React.RefObject<HTMLButtonElement>;
  const searchParams = getSearchParamsClient(searchParamsServer);
  const { imageBasePath } = localization;
  const { controls } = getSortControl({
    searchParams,
    searchMode,
    searchInfoDetailData,
    isListView: true,
    imageBasePath,
    basic: true,
    localization,
    isMobile,
    t,
  });
  const [currentValue, setCurrentValue] = useState<SortValues>(searchParams[URLParameters.SORT] as SortValues);
  const [expand, setExpand] = useState(false);
  const [sortPrice, setSortPrice] = useState(controls?.[0]?.options?.[1]);
  const [sortStar, setSortStar] = useState(controls?.[0]?.options?.[3]);
  const optionsLength = controls?.[0]?.options?.length;

  const handleOnClick = (value: string) => {
    let newSortValue: SortValues = value as SortValues;
    if (value === SortValues.PriceAsc && currentValue === SortValues.PriceAsc) {
      newSortValue = SortValues.PriceDesc;
      setSortPrice(controls?.[0].options?.[2]);
    } else if (value === SortValues.PriceDesc && currentValue === SortValues.PriceDesc) {
      newSortValue = SortValues.PriceAsc;
      setSortPrice(controls?.[0].options?.[1]);
    } else if (value === SortValues.CategoryDesc && currentValue === SortValues.CategoryDesc) {
      newSortValue = SortValues.CategoryAsc;
      setSortStar(controls?.[0].options?.[4]);
    } else if (value === SortValues.CategoryAsc && currentValue === SortValues.CategoryAsc) {
      newSortValue = SortValues.CategoryDesc;
      setSortStar(controls?.[0].options?.[3]);
    } else {
      if (currentValue === SortValues.PriceAsc || currentValue === SortValues.PriceDesc) {
        setSortPrice(controls?.[0].options?.[1]);
      }
      if (currentValue === SortValues.CategoryAsc || currentValue === SortValues.CategoryDesc) {
        setSortStar(controls?.[0].options?.[3]);
      }
    }
    searchParams[URLParameters.SORT] = newSortValue;
    setCurrentValue(newSortValue);

    starTrack.resultPageSortResult(createResultPageSortResultPayload({ searchParams, localization }));
    handleSortChanged({ newSortValue, searchParamsUpdated: searchParams });
    setExpand(false);
  };

  const handleExpand = () => {
    setExpand(!expand);
  };

  useOnclickOutside(
    () => {
      if (expand) {
        setExpand(!expand);
      }
    },
    { refs: [containerRef], ignoreClass: ['listview-sort-overlay'] }
  );

  return (
    <ErrorBoundary id="ListViewSort">
      <ListViewSortContainer>
        <ListViewSortItem
          label={controls?.[0].options?.[0].label}
          value={controls?.[0].options?.[0].value}
          currentValue={currentValue}
          optionsLength={optionsLength}
          handleOnClick={handleOnClick}
          isLoading={isLoading}
        />
        <ListViewSortItem
          label={sortPrice?.label}
          value={sortPrice?.value}
          currentValue={currentValue}
          optionsLength={optionsLength}
          handleOnClick={handleOnClick}
          isLoading={isLoading}
        />
        <ListViewSortItem
          label={sortStar?.label}
          value={sortStar?.value}
          currentValue={currentValue}
          optionsLength={optionsLength}
          handleOnClick={handleOnClick}
          isLoading={isLoading}
        />
        <ListViewSortItem
          label={controls?.[0].options?.[5].label}
          value={controls?.[0].options?.[5].value}
          currentValue={currentValue}
          optionsLength={optionsLength}
          handleOnClick={handleOnClick}
          isLoading={isLoading}
        />
        {optionsLength && optionsLength > 6 && (
          <>
            <Expand
              onClick={isLoading ? undefined : handleExpand}
              ref={containerRef}
              isSelected={currentValue === SortValues.DistanceCity || currentValue === SortValues.DistanceAirport}
              localization={localization}
              isLoading={isLoading}
            >
              {/* Black: the button tints it on hover with a filter tuned for black. */}
              <Icon
                category="UI Essentials"
                name="icon-sort"
                size={20}
                color="#000000"
                alt={t('vacation.oss.components.searchresults.sort.title')}
              />
            </Expand>
            {expand && (
              <Overlay className="listview-sort-overlay">
                {controls?.[0].options?.slice(6, controls[0].options.length).map((option) => (
                  <OverlayItem
                    key={option.value}
                    onClick={() => handleOnClick(option.value)}
                    isSelected={currentValue === option.value}
                    localization={localization}
                    tabIndex={0}
                  >
                    {option.label}
                    {currentValue === option.value && (
                      <Icon
                        category="Semantics"
                        name="icon-check"
                        size={16}
                        color="iconPrimaryDefault"
                        alt={t('vacation.oss.components.searchresults.sort.title')}
                      />
                    )}
                  </OverlayItem>
                ))}
              </Overlay>
            )}
          </>
        )}
      </ListViewSortContainer>
    </ErrorBoundary>
  );
};

export default ListViewSort;
